import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/api/error';
import {
  type CancelSimulationRequest,
  cancelSimulation,
  extractMidPrice,
  getAppInfo,
  getBalances,
  getMarkets,
  getSimulations,
  type MarketMakerState,
  setMakerState,
  setTakerState,
} from '@/services/api/market-maker';

// Legacy cadence: markets + info + simulations poll every 10 s while the page is
// mounted; the dialog mid-price polls every 5 s. Balances are NEVER polled (expensive
// on the service side) — manual refetch only. Intervals are set per-query, never on a
// global default.
const PAGE_POLL_INTERVAL_MS = 10_000;
const PRICE_POLL_INTERVAL_MS = 5_000;
/** Legacy 5 s error-toast duration — shared with new-simulation-dialog's submit-error toast. */
export const ERROR_TOAST_DURATION_MS = 5_000;

export const MARKET_MAKER_QUERY_KEY = ['market-maker'] as const;
export const MARKET_MAKER_MARKETS_QUERY_KEY = [...MARKET_MAKER_QUERY_KEY, 'markets'] as const;
export const MARKET_MAKER_INFO_QUERY_KEY = [...MARKET_MAKER_QUERY_KEY, 'info'] as const;
export const MARKET_MAKER_SIMULATIONS_QUERY_KEY = [...MARKET_MAKER_QUERY_KEY, 'simulations'] as const;
export const MARKET_MAKER_BALANCES_QUERY_KEY = [...MARKET_MAKER_QUERY_KEY, 'balances'] as const;

// ---------------------------------------------------------------------------
// Reads. Poll failures are SILENT by design (legacy never rendered its error ref);
// never toast here — a down sidecar would toast every 10 s.
// ---------------------------------------------------------------------------

export const marketMakerMarketsQueryOptions = () =>
  queryOptions({
    queryKey: MARKET_MAKER_MARKETS_QUERY_KEY,
    queryFn: getMarkets,
    refetchInterval: PAGE_POLL_INTERVAL_MS,
  });

export const useMarketMakerMarkets = () => useQuery(marketMakerMarketsQueryOptions());

export const marketMakerInfoQueryOptions = () =>
  queryOptions({
    queryKey: MARKET_MAKER_INFO_QUERY_KEY,
    queryFn: getAppInfo,
    refetchInterval: PAGE_POLL_INTERVAL_MS,
  });

export const useMarketMakerInfo = () => useQuery(marketMakerInfoQueryOptions());

export const marketMakerSimulationsQueryOptions = () =>
  queryOptions({
    queryKey: MARKET_MAKER_SIMULATIONS_QUERY_KEY,
    queryFn: getSimulations,
    refetchInterval: PAGE_POLL_INTERVAL_MS,
  });

export const useMarketMakerSimulations = () => useQuery(marketMakerSimulationsQueryOptions());

// Balances load only on explicit user click (Refresh button → refetch()).
export const marketMakerBalancesQueryOptions = () =>
  queryOptions({
    queryKey: MARKET_MAKER_BALANCES_QUERY_KEY,
    queryFn: getBalances,
    enabled: false,
  });

export const useMarketMakerBalances = () => useQuery(marketMakerBalancesQueryOptions());

/**
 * Port of legacy useMarketMakerPrice: while `enabled` (dialog open) and a market is
 * selected, fetch immediately then every 5 s; otherwise stop AND report null (never a
 * stale price). Errors are swallowed → null. Feeds the timeline editor's currentPrice.
 */
export const useMarketMakerPrice = (market: string, enabled: boolean): number | null => {
  const isActive = enabled && market !== '';
  const query = useQuery({
    queryKey: [...MARKET_MAKER_QUERY_KEY, 'price', market],
    queryFn: getMarkets,
    refetchInterval: PRICE_POLL_INTERVAL_MS,
    enabled: isActive,
    select: (response) => extractMidPrice(response, market),
  });
  if (!isActive || query.isError) return null;
  return query.data ?? null;
};

// ---------------------------------------------------------------------------
// Mutations. Errors toast (5 s, exact legacy copy); success refetches the read the
// legacy composable awaited (markets for state changes, simulations for sim ops).
// ---------------------------------------------------------------------------

export interface SetStateVariables {
  state: MarketMakerState;
  /** omitted = apply to ALL markets */
  market?: string;
}

export const useSetMakerState = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ state, market }: SetStateVariables) => setMakerState(state, market),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: MARKET_MAKER_MARKETS_QUERY_KEY }),
    onError: (error, { state, market }) => {
      const message = getErrorMessage(error);
      toast.error(market ? `Failed to ${state} ${market}: ${message}` : `Failed to ${state} all markets: ${message}`, { duration: ERROR_TOAST_DURATION_MS });
    },
  });
};

export const useSetTakerState = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ state, market }: SetStateVariables) => setTakerState(state, market),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: MARKET_MAKER_MARKETS_QUERY_KEY }),
    onError: (error, { state, market }) => {
      const message = getErrorMessage(error);
      toast.error(market ? `Failed to ${state} takers on ${market}: ${message}` : `Failed to ${state} takers on all markets: ${message}`, {
        duration: ERROR_TOAST_DURATION_MS,
      });
    },
  });
};

export const useCancelSimulation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CancelSimulationRequest) => cancelSimulation(body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: MARKET_MAKER_SIMULATIONS_QUERY_KEY }),
    onError: (error) => toast.error(`Failed to cancel simulation: ${getErrorMessage(error)}`, { duration: ERROR_TOAST_DURATION_MS }),
  });
};
